import { Autocomplete, Stack, TextField } from "@mui/material";
import { NumericFormatterTextField } from "@platform/design-system";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useMemo } from "react";

import { SummaryField } from "../../BonusSummaryDescription";
import InputDate from "../../../../../components/inputDate";
import {
  FundingType,
  PolicyType,
  POLICY_TYPE_TO_PRODUCT_NAMES,
  type CoverageDetailsFormValues,
} from "../../../constants";
import { CarrierSelect } from "../../carrierSelect";

type CoverageDetailsProps = {
  brokerName: string;
  brokerSk: string;
  lockForm: boolean;
  businessType: string;
};

export const CoverageDetails: React.FC<CoverageDetailsProps> = ({
  brokerName,
  brokerSk,
  lockForm,
  businessType,
}) => {
  const { control, setValue } = useFormContext<CoverageDetailsFormValues>();

  const selectedPolicyType = useWatch({ control, name: "policyType" });

  const policyTypeOptions = Object.entries(PolicyType).map(([key, value]) => ({
    label: key,
    value,
  }));
  const fundingTypeOptions = Object.entries(FundingType).map(
    ([key, value]) => ({
      label: key,
      value,
    }),
  );

  const filteredPlanTypeOptions = useMemo(() => {
    if (!selectedPolicyType) return [];
    const allowedNames =
      POLICY_TYPE_TO_PRODUCT_NAMES[selectedPolicyType.value] ?? [];
    return allowedNames.map((name) => ({ id: name, name }));
  }, [selectedPolicyType]);

  return (
    <>
      <Stack direction="row" spacing={3}>
        <SummaryField
          title="Broker Name"
          description={brokerName}
          layout="stack"
        />
        <SummaryField title="Broker SK" description={brokerSk} layout="stack" />
      </Stack>

      <Controller
        control={control}
        name="carrier"
        render={({ field, fieldState: { error } }) => (
          <CarrierSelect
            {...field}
            value={field.value as { label: string; value: string } | null}
            onChange={field.onChange}
            disabled={lockForm}
            textFieldProps={{ error: !!error, helperText: error?.message }}
          />
        )}
      />

      <Controller
        control={control}
        name="originalSaleDate"
        render={({ field, fieldState: { error } }) => (
          <InputDate
            {...field}
            label="Original Sale Date"
            readOnly={lockForm}
            disabled={lockForm}
            textFieldParams={{ error: !!error, helperText: error?.message }}
          />
        )}
      />

      {businessType === "BOR" && (
        <Controller
          control={control}
          name="premiumEligibleDate"
          render={({ field, fieldState: { error } }) => (
            <InputDate
              {...field}
              label="Premium Eligible Date"
              readOnly={lockForm}
              disabled={lockForm}
              textFieldParams={{ error: !!error, helperText: error?.message }}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="policyType"
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            options={policyTypeOptions}
            getOptionLabel={(option) => option.label}
            value={field.value}
            onChange={(_, newValue) => {
              field.onChange(newValue);
              setValue("planType", null, {
                shouldDirty: true,
                shouldValidate: false,
              });
            }}
            isOptionEqualToValue={(option, value) =>
              value != null && option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Policy Type"
                error={!!error}
                helperText={error?.message}
              />
            )}
            sx={{ flex: 1 }}
          />
        )}
      />

      <Controller
        control={control}
        name="planType"
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            options={filteredPlanTypeOptions}
            getOptionLabel={(option) => option.name}
            value={field.value}
            onChange={(_, newValue) => field.onChange(newValue)}
            isOptionEqualToValue={(option, value) =>
              value != null && option.id === value.id
            }
            disabled={!selectedPolicyType}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Plan Type"
                error={!!error}
                helperText={error?.message}
              />
            )}
            sx={{ flex: 1 }}
          />
        )}
      />

      <Controller
        control={control}
        name="averageMonthlyPremium"
        render={({ field, fieldState: { error } }) => (
          <NumericFormatterTextField
            {...field}
            fullWidth
            decimalScale={2}
            label="Average Monthly Premium"
            prefix={"$"}
            fixedDecimalScale
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="fundingType"
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            options={fundingTypeOptions}
            getOptionLabel={(option) => option.label}
            value={field.value}
            onChange={(_, newValue) => field.onChange(newValue)}
            isOptionEqualToValue={(option, value) =>
              value != null && option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Funding Type"
                error={!!error}
                helperText={error?.message}
              />
            )}
            sx={{ flex: 1 }}
          />
        )}
      />

      <Controller
        control={control}
        name="participants"
        render={({ field, fieldState: { error } }) => (
          <NumericFormatterTextField
            {...field}
            fullWidth
            decimalScale={0}
            label="Participants"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name="brokerSplit"
        render={({ field, fieldState: { error } }) => (
          <NumericFormatterTextField
            {...field}
            fullWidth
            decimalScale={0}
            label="Broker Split"
            suffix="%"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
    </>
  );
};
